import {AfterViewInit, Component, EventEmitter, inject, Inject, Input, OnInit, Output, signal, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatTabChangeEvent} from '@angular/material/tabs';
import {TranslateService} from '@ngx-translate/core';
import {HtmlEditorService, ImageService, LinkService, RichTextEditor, ToolbarService} from '@syncfusion/ej2-angular-richtexteditor';
import {BenutzerModels} from 'src/app/models/benutzer-models';
import {GlobalModels} from 'src/app/models/global-models';
import {ServiceAppModels} from 'src/app/models/serviceapp-models';
import {DatumFormatPipe} from 'src/app/pipes/datum-format.pipe';
import {MessageBoxService} from 'src/app/services/message-box.service';
import {ServiceAppService} from 'src/app/services/service-app.service';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {map, startWith, tap} from 'rxjs';
import {SwissSettingsService} from '../../../services/swiss-settings.service';
import {SwissAppConfigModels} from '../../../models/swissappconfig-models';
import SwissAppConfig = SwissAppConfigModels.SwissAppConfig;
import {catchError} from "rxjs/operators";


@Component({
    selector: 'app-termin-details',
    templateUrl: './termin-details.component.html',
    styleUrls: ['./termin-details.component.scss'],
    providers: [ToolbarService, LinkService, ImageService, HtmlEditorService,]
})
export class TerminDetailsComponent implements OnInit, AfterViewInit {
    @Output() close = new EventEmitter<any>();
    @Output() submit = new EventEmitter<any>();
    svcSwissSettings = inject(SwissSettingsService);
    public currTermin: ServiceAppModels.SA_Objekt;
    public currTerminBackup: ServiceAppModels.SA_Objekt;
    /* Felder */
    fldNotizenKey = 'fldNotizen';
    fldProdDescKey = 'fldProdDesc';
    fldZusArbeitenKey = 'fldZusArbeiten';
    fldMemoKey = 'fldMemo';
    fldMemoAddKey = 'fldMemoAdd';
    fldDatumKey = 'fldDatum';
    fldStartzeitKey = 'fldStartzeit';
    fldEndzeitKey = 'fldEndzeit';
    protected readonly isManuDescRichText = toSignal(
        this.svcSwissSettings.getServiceAppConfig().pipe(
            map((config: SwissAppConfig) => config.richTextFormat_ManuDesc === 1),
            catchError((err, caught) => caught.pipe(startWith(undefined)))
        ), {initialValue: undefined}
    );
    protected readonly isSaMemoRichText = toSignal(
        this.svcSwissSettings.getServiceAppConfig().pipe(
            map((config: SwissAppConfig) => config.richTextFormat_SA_Memo === 1),
            catchError((err, caught) => caught.pipe(startWith(undefined)))
        ), {initialValue: undefined}
    );
    @Input() formGroupDetails: UntypedFormGroup;
    @ViewChild('richTxtEditor1') rteObj1?: RichTextEditor;
    @ViewChild('richTxtEditor2') rteObj2?: RichTextEditor;
    @ViewChild('richTxtEditor3') rteObj3?: RichTextEditor;
    /* Register Adressen */
    public kundeDetail: ServiceAppModels.SA_KundeDetail;
    public kundeKontaktDetail: ServiceAppModels.SA_KundeKontaktDetail;
    public rechnungDetail: ServiceAppModels.SA_RechnungsAdresseDetail;
    public rechnungKontaktDetail: ServiceAppModels.SA_RechnungsAdresseKontaktDetail;
    public architektDetail: ServiceAppModels.SA_ArchitektAdresseDetail;
    public architektKontaktDetail: ServiceAppModels.SA_ArchitektAdresseKontaktDetail;
    public lieferantDetail: ServiceAppModels.SA_LieferantAdresseDetail;
    public lieferantKontaktDetail: ServiceAppModels.SA_LieferantAdresseKontaktDetail;
    public hasError = false;
    /* RichtText Editor */
    public tools: object = {
        items: ['Undo', 'Redo', '|',
            'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
            'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
            'SubScript', 'SuperScript', '|',
            'LowerCase', 'UpperCase', '|',
            'Formats', 'Alignments', '|', 'OrderedList', 'UnorderedList', '|',
            'Indent', 'Outdent', '|', 'CreateLink',
            'Image', '|', 'ClearFormat', 'Print', 'SourceCode', '|', 'FullScreen']
    };
    // Höhe der Richtext-Boxen
    public height = 100;
    private currAnwender?: BenutzerModels.Anwender;
    private sa_AppointmentDetails?: ServiceAppModels.SA_AppointmentDetails;

    constructor(private svcSA: ServiceAppService,
                private _datumFormatPipe: DatumFormatPipe,
                private svcMessageBox: MessageBoxService,
                public dialogRef: MatDialogRef<TerminDetailsComponent>,
                private translate: TranslateService,
                @Inject(MAT_DIALOG_DATA) public data: ServiceAppModels.SA_DataForAppointment) {
        this.currTermin = this.data.sA_Termin!;
        this.currTerminBackup = this.data.sA_Termin!;
        this.currAnwender = this.data.sA_Anwender;


        this.formGroupDetails = new UntypedFormGroup({
            fldDatum: new UntypedFormControl(),
            fldStartzeit: new UntypedFormControl(),
            fldEndzeit: new UntypedFormControl(),
            fldNotizen: new UntypedFormControl(),
            fldProdDesc: new UntypedFormControl(),
            fldZusArbeiten: new UntypedFormControl(),
            fldMemo: new UntypedFormControl(),
            fldMemoAdd: new UntypedFormControl()
        });

        this.kundeDetail = this.svcSA.getAdresseDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Kunde);
        this.kundeKontaktDetail = this.svcSA.getAdresseKontaktDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Kunde);
        this.rechnungDetail = this.svcSA.getAdresseDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Rechnungsadresse);
        this.rechnungKontaktDetail = this.svcSA.getAdresseKontaktDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Rechnungsadresse);
        this.architektDetail = this.svcSA.getAdresseDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Architekt);
        this.architektKontaktDetail = this.svcSA.getAdresseKontaktDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Architekt);
        this.lieferantDetail = this.svcSA.getAdresseDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Lieferant);
        this.lieferantKontaktDetail = this.svcSA.getAdresseKontaktDetail(this.currTermin.objectID, GlobalModels.AdressTypes.Lieferant);
    }

    ngAfterViewInit(): void {
    }

    ngOnInit(): void {
        this.loadFormValues();
        this.changePosition();
    }

    loadFormValues(): void {
        if (this.currTermin) {
            if (this.currTermin.startTime) {
                const datum = this._datumFormatPipe.removeTimeFromDate(new Date(this.currTermin.startTime));
                this.formGroupDetails.controls[this.fldDatumKey].setValue(datum);
                const startzeit = this._datumFormatPipe.transformOnlyTime(this.currTermin.startTime);
                this.formGroupDetails.controls[this.fldStartzeitKey].setValue(startzeit);
                const endzeit = this._datumFormatPipe.transformOnlyTime(this.currTermin.endTime);
                this.formGroupDetails.controls[this.fldEndzeitKey].setValue(endzeit);
            }

            if (this.currTermin?.notes) {
                this.formGroupDetails.controls[this.fldNotizenKey].setValue(this.currTermin?.notes);
            }

            if (this.currTermin?.manufacturingDescription) {
                this.formGroupDetails.controls[this.fldProdDescKey].setValue(this.currTermin?.manufacturingDescription);
            }

            if (this.currTermin?.additionalWork) {
                this.formGroupDetails.controls[this.fldZusArbeitenKey].setValue(this.currTermin?.additionalWork);
            }
            if (this.currTermin.serviceComplete) {
                this.formGroupDetails.controls[this.fldZusArbeitenKey].disable();
            }

            if (this.currTermin?.sA_Memo) {
                this.formGroupDetails.controls[this.fldMemoKey].setValue(this.currTermin?.sA_Memo);
            }

            if (this.currTermin?.sA_Memo_Add) {
                this.formGroupDetails.controls[this.fldMemoAddKey].setValue(this.currTermin?.sA_Memo_Add);
            }
        }
    }

    changePosition() {
        this.dialogRef.updatePosition({top: '5px'});
    }

    onSelected(tabChangeEvent: MatTabChangeEvent) {
        if (tabChangeEvent.index === 2) {
            if (this.rteObj2 && this.rteObj3 && this.isSaMemoRichText()) {
                this.rteObj2.refreshUI();
                this.rteObj3.refreshUI();
            }
        }
    }

    save() {
        if (this.formGroupDetails.value) {
            this.sa_AppointmentDetails = {
                rowID: this.currTermin.rowID,
                serviceID: this.currTermin.serviceID,
                datum: this.formGroupDetails.controls[this.fldDatumKey].value,
                startzeit: this.formGroupDetails.controls[this.fldStartzeitKey].value,
                endzeit: this.formGroupDetails.controls[this.fldEndzeitKey].value,
                dateOrTimeModified: true,
                note: this.formGroupDetails.controls[this.fldNotizenKey].value,
                additionalWork: this.formGroupDetails.controls[this.fldZusArbeitenKey].value,
                sa_Memo_Add: this.formGroupDetails.controls[this.fldMemoAddKey].value
            };

            let datumBackupStart = this.currTerminBackup.startTime!;
            let datumBackupEnd = this.currTerminBackup.endTime!;
            let year = 0;
            let month = 0;
            let day = 0;

            if (datumBackupStart instanceof Date) {
                year = datumBackupStart.getFullYear();
                month = datumBackupStart.getMonth();
                day = datumBackupStart.getDate();
            } else {
                datumBackupStart = new Date(datumBackupStart);
                year = datumBackupStart.getFullYear();
                month = datumBackupStart.getMonth();
                day = datumBackupStart.getDate();
            }
            const datum = new Date(year, month, day, 0, 0, 0);

            if (datumBackupEnd instanceof Date) {

            } else {
                datumBackupEnd = new Date(datumBackupEnd);
            }

            if (this.sa_AppointmentDetails.datum.toISOString() === datum.toISOString() &&
                this.sa_AppointmentDetails.startzeit === this._datumFormatPipe.transformOnlyTime(datumBackupStart) &&
                this.sa_AppointmentDetails.endzeit === this._datumFormatPipe.transformOnlyTime(datumBackupEnd)) {
                // Datum/Zeit wurde nicht angepasst
                this.sa_AppointmentDetails.dateOrTimeModified = false;
            }
        }
        this.dialogRef.close(this.sa_AppointmentDetails);
    }

    delEinsatz() {
        this.svcMessageBox
            .askYesNoQuestion(
                'MAIN_APP_DIALOG.DEL_DEPLOYMENT_PLANING.msg',
                'MAIN_APP_DIALOG.DEL_DEPLOYMENT_PLANING.title'
            )
            .then((value) => {
                if (value) {
                    const currAssignment: ServiceAppModels.Assignment = {
                        employeeID: Number(this.currTermin.fK_EmployeeID),
                        mandator: this.currTermin.mandator,
                        rowID: this.currTermin.rowID,
                        serviceID: this.currTermin.serviceID,
                        objectID: this.currTermin.objectID,
                        day: undefined,
                        startTime: undefined,
                        endTime: undefined
                    };
                    this.svcSA.delEmpWorkPlanEntry(currAssignment).subscribe({
                        next: async (res) => {
                            if (res) {
                                // Der Agena mitteilen, dass der zuletzt verwendete Termin entfernt werden muss
                                await this.svcSA.removeTermin();
                                // Gelöschter Einsatz wurde erfolgreich entfernt, nun muss dieser aus der Agenda gelöscht werden.
                                // Sobald dieser gelöscht ist, ruft der SA-Service die Methode loadOrders auf
                                await this.svcSA.delAuftragByRowID(currAssignment.rowID);
                                this.dialogRef.close(null);
                            } else {
                                this.translate.get('MAIN_APP_DIALOG.ERROR_DIALOG.DELETE_ORDER_NOT_POSSIIBLE.text').subscribe((t: string) => {
                                    this.svcMessageBox.showError(t, '');
                                });
                                this.dialogRef.close(null);
                            }
                        },
                        error: (error) => {
                            this.translate.get('MAIN_APP_DIALOG.ERROR_DIALOG.DELETE_ORDER_NOT_POSSIIBLE.text').subscribe((t: string) => {
                                this.svcMessageBox.showError(t, '');
                            });
                        },
                    });
                }
            });
    }
}
